import { Component, computed, effect, inject, input } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { EventFormStore } from "../event.form.store";
import { S25SimpleCollapseComponent } from "../../../modules/s25-simple-collapse/s25.simple.collapse.component";
import { Item } from "../../../pojo/Item";
import { EventFormObjectAvailabilityComponent } from "../object-availability/event.form.object.availability.component";
import { EventFormObjectDetailsList } from "./object.details.list.component";
import { S25Date } from "../../../util/core/s25.date";
import { EventForm } from "..";

const RULE_PERMISSION_MESSAGE =
    "You do not have permissions to request or reserve a resource being added with a rule. Please contact an administrator for assistance.";

/**
 * Shared across chooser instances so two components bound to the same field never stack the check twice.
 * TODO: move this into event form store or hoist up to event-form-item
 */
const validatedFields = new WeakSet<EventForm.ObjectFieldModel>();

@TypeManagerDecorator("s25-event-form-object-chooser")
@Component({
    selector: "s25-event-form-object-chooser",
    standalone: true,
    imports: [EventFormObjectAvailabilityComponent, EventFormObjectDetailsList, S25SimpleCollapseComponent],
    template: `
        @if (itemTypeId()) {
            @if (privateMessage()) {
                <div>{{ privateMessage() }}</div>
            }

            @if (canSearch()) {
                <s25-simple-collapse
                    class="rose-form--collapse rose-form--collapse__object-search"
                    [header]="{ text: searchTitle(), level: 2 }"
                >
                    <fieldset class="rose-form--collapseFieldset">
                        <legend class="visuallyhidden">{{ searchTitle() }}</legend>
                        <event-form-object-availability
                            [itemTypeId]="itemTypeId()"
                            [model]="eventForm.model()"
                            (itemsAdded)="onItemsAdded()"
                        />
                    </fieldset>
                </s25-simple-collapse>
            }

            @if (eventInitialized()) {
                <!-- Show the list but hide it even if it has no items,
                 because it has jenky angularJS-dependent mounting logic -->
                <s25-event-form-object-details-list
                    [hidden]="!items().length"
                    [itemTypeId]="itemTypeId()"
                    [items]="items()"
                />
            }
        }
    `,
    styles: `
        @property --object-validation-color {
            syntax: "<color>";
            inherits: true;
            initial-value: initial;
        }

        s25-simple-collapse {
            --simple-collapse--highlight-color: var(--object-validation-color, transparent);
            --simple-collapse--border-color: color-mix(
                in oklch,
                var(--color--border) 75%,
                var(--object-validation-color, var(--color--border))
            );
            --simple-collapse--bg-color: var(--color--surface-bg);
            --simple-collapse--header-bg-color: color-mix(
                in srgb,
                var(--color--surface-2-bg) 93%,
                var(--object-validation-color, var(--color--surface-2-bg)) 7%
            );
            --simple-collapse--padding: 0 1em;
        }
        /* hyper specific to override other hyper-specific rules :( */
        :host-context(#s25) s25-simple-collapse ::ng-deep button.ngAnchor.ngAnchor {
            color: var(--object-validation-color) !important;
        }

        :host ::ng-deep .rose-added-object-table {
            .cn-form__control {
                max-width: 60%;
            }
        }
    `,
})
export class EventFormObjectChooser {
    readonly model = input.required<EventForm.ObjectFieldModel>();

    readonly eventForm = inject(EventFormStore);

    /** The location field is still called "space" on the AngularJS form model. */
    private readonly isLocation = computed(() => this.model()?.uuid === "space");
    private readonly isResource = computed(() => this.model()?.uuid === "resource");

    /** Null for any other field type, which renders nothing. */
    readonly itemTypeId = computed(() => {
        if (this.isLocation()) return Item.Ids.Location;
        if (this.isResource()) return Item.Ids.Resource;
        return null;
    });

    readonly privateMessage = computed(() => {
        const formModel = this.eventForm.model();
        if (this.isLocation() && formModel.hasPrivateLocations) {
            return "All or some locations on this event are hidden due to permissions";
        }
        if (this.isResource() && formModel.hasPrivateResources) {
            return "All or some resources on this event are hidden due to permissions";
        }
        return "";
    });

    readonly canSearch = computed(() => {
        const formModel = this.eventForm.model();
        if (this.isLocation()) return !!formModel.canSearchLocations;
        return this.isResource() && !!formModel.canSearchResources;
    });

    readonly searchTitle = computed(() => {
        const formModel = this.eventForm.model();
        if (this.isLocation()) return formModel.hasRecommendedLocation ? "Recommended Locations" : "Locations Search";
        if (this.isResource()) return formModel.hasRecommendedResource ? "Recommended Resources" : "Resources Search";
        return "";
    });

    readonly items = computed<EventForm.ObjectItem[]>(() => {
        const formModel = this.eventForm.model();
        if (this.isLocation()) return formModel.locations ?? [];
        if (this.isResource()) return formModel.resources ?? [];
        return [];
    });

    readonly eventInitialized = computed(() => !!this.eventForm.model().initialDatetimePickerInit);

    constructor() {
        effect(() => {
            // TODO: hoist this up higher and consolidate when we migrate event-form-item
            this.attachFieldValidation(this.model());
        });
    }

    onItemsAdded(): void {
        this.model()?.action?.();
        this.eventForm.bump();
    }

    /**
     * Adds assignment-policy and advance-notice checks to the location or
     * resource field's own `validate`.
     * TODO: validation almost certainly does not belong in this component
     */
    private attachFieldValidation(fieldModel: EventForm.ObjectFieldModel): void {
        if (validatedFields.has(fieldModel)) return;
        validatedFields.add(fieldModel);

        const defaultValidation = fieldModel.validate;
        fieldModel.validate = () => {
            const objectErrors = this.validateObject(fieldModel, this.eventForm.model());
            return objectErrors ?? defaultValidation?.() ?? true;
        };
    }

    /** Returns the blocking errors for the field, or `undefined` when every occurrence is allowed. */
    private validateObject(
        fieldModel: EventForm.ObjectFieldModel,
        formModel: EventForm,
    ): EventForm.ObjectFieldValidationError[] | undefined {
        const isLocationField = fieldModel.uuid === "space";
        const objectNoun = isLocationField ? "location" : "resource";
        const isOverridden = isLocationField ? formModel.overrideRmAp : formModel.overrideRsAp;
        if (isOverridden) return undefined;

        const currentDateTime = S25Date.currentDateTime();
        const objOccs = isLocationField ? formModel.getLocOccs() : formModel.getResOccs();

        for (const objOcc of objOccs) {
            if (!objOcc.isIncluded) continue;

            if (EventForm.ObjectOccurrence.blockedByAssignmentPolicy(objOcc)) {
                if (fieldModel.forceHidden && objOcc.preventRuleQuantity) {
                    return [{ message: RULE_PERMISSION_MESSAGE, target: fieldModel.uuid }];
                }
                return [
                    {
                        message: `Some ${objectNoun}s can only be requested at certain times. In order to save, please adjust the dates or ${objectNoun}s.`,
                        target: fieldModel.uuid,
                    },
                ];
            }

            if (EventForm.ObjectOccurrence.needsAdvanceNotice(objOcc, currentDateTime)) {
                return [
                    {
                        message: `Some ${fieldModel.uuid}s require an advanced notice to request. In order to save, please adjust the dates or ${fieldModel.uuid}s.`,
                        target: fieldModel.uuid,
                    },
                ];
            }
        }

        return undefined;
    }
}
